import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { addressApi, paymentsApi, loadRazorpay, shortOrderId } from "../api/client.js";
import { cartLines, formatPrice, WHATSAPP_NUMBER, deliveryFeeFor, FREE_DELIVERY_MIN } from "../data/products.js";
import AddressForm from "../components/AddressForm.jsx";
import { FarmLoaderOverlay } from "../components/FarmLoader.jsx";
import Seo from "../components/Seo.jsx";

// Online payment (Razorpay) only — no Cash on Delivery, no WhatsApp-coordinated
// UPI/bank transfer. Every order is paid for up front; an unpaid order is
// auto-cancelled by the server-side sweep if payment isn't completed in time.
const PAYMENTS = [
  { id: "razorpay", label: "Pay online", desc: "Cards, UPI, wallets & netbanking via Razorpay.", tag: "Recommended", enabled: true },
];

const STEPS = [
  { n: 1, label: "Address" },
  { n: 2, label: "Payment" },
  { n: 3, label: "Review" },
];

export default function Checkout() {
  const { items, clear } = useCart();
  const { user, placeOrder, membership, refreshOrders } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);
  const [addresses, setAddresses] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [adding, setAdding] = useState(false);
  const [payment, setPayment] = useState("razorpay");
  const [error, setError] = useState("");
  const [restricted, setRestricted] = useState(false);
  const [offerClaimed, setOfferClaimed] = useState(false);
  const [busy, setBusy] = useState(false);

  // The backend returns 403 with this text when an account is blocked from ordering.
  const isRestrictedError = (msg) => /not permitted to place orders/i.test(msg || "");
  // 409 when the ₹9 founding-member plan has already been claimed by this
  // account or phone number — one per household.
  const isOfferClaimedError = (msg) => /founding-member offer is limited/i.test(msg || "");

  const loadAddresses = async () => {
    try {
      const { addresses: list } = await addressApi.list();
      setAddresses(list || []);
      const def = (list || []).find((a) => a.isDefault) || (list || [])[0];
      if (def) setSelectedId(def.id);
      setAdding((list || []).length === 0);
    } catch {
      setAddresses([]);
      setAdding(true);
    }
  };

  useEffect(() => {
    if (user) void loadAddresses();
  }, [user]);

  const payOptions = PAYMENTS;

  const lines = cartLines(items).map((l) => ({
    // `id` stays the plain product id for the order's productId; the pack size
    // travels in `unit` ("1 L pack") and in the display name. `key` stays
    // unique per size so two packs of the same oil don't collide as React keys.
    key: l.key,
    id: l.product.id,
    name: l.sizeLabel ? `${l.name} (${l.sizeLabel})` : l.name,
    // The size already sits in the name, so the unit is just the countable
    // thing — otherwise emails read "2 1 L pack × Groundnut Oil (1 L)".
    unit: l.sizeLabel ? "pack" : l.unit,
    pricePerKg: l.price,
    qty: l.qty,
    lineTotal: l.lineTotal,
  }));
  const total = lines.reduce((a, l) => a + l.lineTotal, 0);
  const itemCount = lines.reduce((a, l) => a + l.qty, 0);

  // Delivery rule lives in products.js (shared with the Cart page's nudge).
  const deliveryFee = deliveryFeeFor(lines, { memberFreeDelivery: membership?.active });
  const payable = total + deliveryFee;

  if (!lines.length) {
    return (
      <section className="section">
        <div className="container cart-empty">
          <div className="cart-empty__icon">🛒</div>
          <h2>Nothing to check out</h2>
          <Link to="/products" className="btn btn--primary btn--lg">Browse products</Link>
        </div>
      </section>
    );
  }

  // Buying requires an account — saved addresses, order tracking and payment
  // all key off it, and it's also how we know who to honor the founding-member
  // plan for. Cart contents live in localStorage, so nothing is lost while the
  // customer signs in or signs up and comes back.
  if (!user) {
    return (
      <section className="section">
        <Seo title="Checkout" path="/checkout" noindex />
        <div className="container cart-empty">
          <div className="cart-empty__icon">🔒</div>
          <h2>Sign in to check out</h2>
          <p>Create a free account or sign in to save your address, track your order, and pay securely.</p>
          <div className="cart-empty__actions">
            <Link to="/signup?next=/checkout" className="btn btn--primary btn--lg">Create account</Link>
            <Link to="/login?next=/checkout" className="btn btn--ghost btn--lg">Sign in</Link>
          </div>
        </div>
      </section>
    );
  }

  const selectedAddress = addresses.find((a) => a.id === selectedId);

  const addressSummary = selectedAddress
    ? `${selectedAddress.line1}${selectedAddress.line2 ? ", " + selectedAddress.line2 : ""}, ${selectedAddress.city}, ${selectedAddress.state} ${selectedAddress.pincode}`
    : "";

  const addAddress = async (payload) => {
    try {
      const { address } = await addressApi.create(payload);
      await loadAddresses();
      if (address?.id) setSelectedId(address.id);
      setAdding(false);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  };

  // Move from Address -> Payment after validating the delivery details.
  const continueFromAddress = () => {
    setError("");
    if (adding) { setError("Please save the address first, or pick a saved one."); return; }
    if (!selectedId) { setError("Please select or add a delivery address."); return; }
    setStep(2);
  };

  const continueFromPayment = () => {
    setError("");
    const chosen = payOptions.find((p) => p.id === payment);
    if (!chosen || !chosen.enabled) { setError("Please choose a valid payment method."); return; }
    setStep(3);
  };

  const submit = async () => {
    setError("");
    setRestricted(false);
    setOfferClaimed(false);

    if (!selectedId) { setStep(1); setError("Please select or add a delivery address."); return; }
    const a = selectedAddress;
    const orderArgs = {
      lines, total: payable, addressId: selectedId, paymentMethod: "online",
      name: a?.name, phone: a?.phone, address: addressSummary,
    };

    setBusy(true);
    const res = await placeOrder(orderArgs);
    if (!res.ok) {
      setBusy(false);
      if (isRestrictedError(res.error)) { setRestricted(true); setError(""); }
      else if (isOfferClaimedError(res.error)) { setOfferClaimed(true); setError(""); }
      else setError(res.error || "Could not place your order.");
      return;
    }

    await payOnline(res.order, orderArgs);
  };

  const payOnline = async (order, orderArgs) => {
    const ok = await loadRazorpay();
    if (!ok || !window.Razorpay) {
      setBusy(false);
      setError("Could not load the payment library. Please try again.");
      return;
    }
    let session;
    try {
      session = await paymentsApi.createOrder(order.id);
    } catch (e) {
      setBusy(false);
      if (isRestrictedError(e.message)) { setRestricted(true); setError(""); }
      else setError(e.message || "Could not start payment. Please try again.");
      return;
    }
    const rzp = new window.Razorpay({
      key: session.keyId,
      order_id: session.razorpayOrderId,
      amount: session.amount,
      currency: session.currency,
      name: "Rimmon Farm",
      description: `Order ${shortOrderId(order.id)}`,
      prefill: {
        name: session.customerName || orderArgs.name || user?.name || "",
        email: user?.email || "",
        contact: String(session.customerPhone || orderArgs.phone || user?.phone || "").replace(/\D/g, "").slice(-10),
      },
      // Show UPI first — the most common method for Indian customers — so the
      // overlay lands on the fastest path instead of a method chooser. The UPI
      // block is restricted to the "qr" flow so it opens straight on a
      // scan-and-pay QR (Razorpay-hosted, order-specific amount baked in —
      // same branding as their static payment-page QR, but per-order and
      // auto-verified through the same verify()/webhook path below, no manual
      // reconciliation). Netbanking stands in for "bank transfer" — Razorpay
      // has no separate NEFT/IMPS flow, but netbanking is the direct
      // bank-to-bank equivalent and is verified the same automatic way.
      config: {
        display: {
          blocks: {
            upi: { name: "Scan & pay with any UPI app", instruments: [{ method: "upi", flows: ["qr"] }] },
            banktransfer: { name: "Bank transfer", instruments: [{ method: "netbanking" }] },
          },
          sequence: ["block.upi", "block.banktransfer"],
          preferences: { show_default_blocks: true },
        },
      },
      // Let the customer retry a failed attempt inside the same overlay instead
      // of it closing and forcing them to restart the whole checkout.
      retry: { enabled: true, max_count: 3 },
      theme: { color: "#2e7d32", backdrop_color: "rgba(20, 40, 22, 0.55)" },
      modal: {
        // Guard against an accidental click outside dismissing a payment in progress.
        confirm_close: true,
        ondismiss: () => {
          setBusy(false);
          setError("Payment cancelled. Your order is saved as unpaid — you can retry.");
        },
      },
      handler: async (response) => {
        try {
          await paymentsApi.verify({
            orderId: order.id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          // Pull the now-paid order into local state before navigating —
          // otherwise "My orders" keeps showing the pre-payment snapshot from
          // placeOrder() until the next full page load re-fetches it.
          await refreshOrders();
          clear();
          navigate(`/order-success?id=${order.id}`);
        } catch (e) {
          setBusy(false);
          setError(e.message || "Payment could not be verified. Contact support if you were charged.");
        }
      },
    });
    rzp.on("payment.failed", (resp) => {
      setBusy(false);
      setError(resp?.error?.description || "Payment failed. Please try again.");
    });
    rzp.open();
  };

  const payLabel = PAYMENTS.find((p) => p.id === payment)?.label || payment;

  return (
    <section className="section co-page">
      <Seo title="Checkout" path="/checkout" noindex />
      <FarmLoaderOverlay show={busy} message={payment === "razorpay" ? "Starting secure payment…" : "Placing your order…"} />
      <div className="container">
        {/* Myntra-style checkout header: centered stepper + secure badge */}
        <div className="co-topbar">
          <ol className="co-steps">
            {STEPS.map((s, i) => (
              <li
                key={s.n}
                className={`co-step ${step === s.n ? "is-active" : ""} ${step > s.n ? "is-done" : ""}`}
              >
                {i > 0 && <span className="co-step__line" aria-hidden="true" />}
                <span className="co-step__label">{s.label}</span>
              </li>
            ))}
          </ol>
          <div className="co-secure">
            <svg className="co-secure__icon" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path fill="currentColor" d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3Zm-1.2 13.2L7.5 12l1.4-1.4 1.9 1.9 4.1-4.1L16.3 9l-5.5 6.2Z"/>
            </svg>
            100% SECURE
          </div>
        </div>

        {restricted && (
          <div className="co-restricted" role="alert">
            <span className="co-restricted__icon">🚫</span>
            <div>
              <strong>Account restricted</strong>
              <p>
                Your account isn’t permitted to place orders right now. If you think this is a
                mistake, please contact us on{" "}
                <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener">WhatsApp</a>{" "}
                and we’ll help sort it out.
              </p>
            </div>
          </div>
        )}

        {offerClaimed && (
          <div className="co-restricted co-restricted--offer" role="alert">
            <span className="co-restricted__icon">🎁</span>
            <div>
              <strong>Offer already claimed</strong>
              <p>
                The ₹9 founding-member offer is limited to one per household, and it looks like
                it's already been claimed on this account or phone number. Remove it from your bag
                to continue, or message us on{" "}
                <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noopener">WhatsApp</a>{" "}
                if you think this is a mistake.
              </p>
            </div>
          </div>
        )}

        <div className="checkout">
          <div className="checkout__main">
            {/* ---------- STEP 1: ADDRESS ---------- */}
            <div className={`co-card ${step === 1 ? "" : "co-card--collapsed"}`}>
              <div className="co-card__head">
                <h2><span className="co-card__num">1</span> Delivery address</h2>
                {step > 1 && addressSummary && (
                  <button type="button" className="co-card__change" onClick={() => setStep(1)}>Change</button>
                )}
              </div>

              {step === 1 ? (
                <div className="co-card__body">
                  <div className="co-addr-list">
                    {addresses.map((a) => (
                      <label key={a.id} className={`co-addr ${selectedId === a.id ? "co-addr--sel" : ""}`}>
                        <input type="radio" name="addr" checked={selectedId === a.id} onChange={() => setSelectedId(a.id)} />
                        <span>
                          <strong>{a.label || "Address"} {a.isDefault && <em className="addr-card__default">Default</em>}</strong>
                          {a.name} · {a.phone}<br />
                          {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.pincode}
                        </span>
                      </label>
                    ))}
                  </div>
                  {adding ? (
                    <div className="co-addform"><AddressForm onSave={addAddress} onCancel={addresses.length ? () => setAdding(false) : undefined} submitLabel="Save & use" /></div>
                  ) : (
                    <button type="button" className="btn btn--ghost" onClick={() => setAdding(true)}>+ Add new address</button>
                  )}
                  {error && step === 1 && <p className="auth__error">{error}</p>}
                  {!adding && (
                    <button type="button" className="btn btn--primary btn--lg co-continue" onClick={continueFromAddress}>
                      Deliver to this address
                    </button>
                  )}
                </div>
              ) : (
                addressSummary && (
                  <p className="co-card__summary">
                    <strong>{selectedAddress?.name}</strong> · {selectedAddress?.phone}<br />
                    {addressSummary}
                  </p>
                )
              )}
            </div>

            {/* ---------- STEP 2: PAYMENT ---------- */}
            <div className={`co-card ${step === 2 ? "" : "co-card--collapsed"}`}>
              <div className="co-card__head">
                <h2><span className="co-card__num">2</span> Payment method</h2>
                {step > 2 && <button type="button" className="co-card__change" onClick={() => setStep(2)}>Change</button>}
              </div>

              {step === 2 ? (
                <div className="co-card__body">
                  <div className="co-pay-list">
                    {payOptions.map((p) => (
                      <label key={p.id} className={`co-pay ${payment === p.id ? "co-pay--sel" : ""} ${p.enabled ? "" : "co-pay--off"}`}>
                        <input type="radio" name="pay" disabled={!p.enabled} checked={payment === p.id} onChange={() => p.enabled && setPayment(p.id)} />
                        <span>
                          <strong>{p.label}{p.tag && p.enabled && <em className="co-pay__tag">{p.tag}</em>}</strong>
                          {p.enabled ? p.desc : "Sign in to pay online."}
                        </span>
                      </label>
                    ))}
                  </div>
                  {error && step === 2 && <p className="auth__error">{error}</p>}
                  <div className="co-actions">
                    <button type="button" className="btn btn--ghost" onClick={() => setStep(1)}>Back</button>
                    <button type="button" className="btn btn--primary btn--lg" onClick={continueFromPayment}>Continue</button>
                  </div>
                </div>
              ) : (
                step > 2 && <p className="co-card__summary"><strong>{payLabel}</strong></p>
              )}
            </div>

            {/* ---------- STEP 3: REVIEW ---------- */}
            <div className={`co-card ${step === 3 ? "" : "co-card--collapsed"}`}>
              <div className="co-card__head">
                <h2><span className="co-card__num">3</span> Review &amp; place order</h2>
              </div>
              {step === 3 && (
                <div className="co-card__body">
                  <div className="co-review">
                    {lines.map((l) => (
                      <div className="co-review__item" key={l.key}>
                        <span>{l.name} <em>× {l.qty} {l.unit}</em></span>
                        <span>{formatPrice(l.lineTotal)}</span>
                      </div>
                    ))}
                  </div>
                  {error && step === 3 && <p className="auth__error">{error}</p>}
                  <div className="co-actions">
                    <button type="button" className="btn btn--ghost" onClick={() => setStep(2)}>Back</button>
                    <button className="btn btn--primary btn--block btn--lg" onClick={submit} disabled={busy || restricted || offerClaimed}>
                      {busy ? "Processing…" : restricted ? "Ordering restricted" : offerClaimed ? "Offer already claimed" : payment === "razorpay" ? `Pay ${formatPrice(payable)} securely` : "Place order"}
                    </button>
                  </div>
                  {busy && payment === "razorpay" && (
                    <p className="co-pay-hint">🔒 Opening the secure payment window — complete your payment there, and you'll come right back here.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ---------- PRICE DETAILS (sticky) ---------- */}
          <aside className="co-summary">
            <h3 className="co-summary__title">Price details</h3>
            <div className="co-summary__rows">
              <div className="co-summary__row">
                <span>Price ({itemCount} {itemCount === 1 ? "item" : "items"})</span>
                <span>{formatPrice(total)}</span>
              </div>
              <div className="co-summary__row">
                <span>Delivery</span>
                {deliveryFee > 0
                  ? <span>{formatPrice(deliveryFee)}</span>
                  : <span className="co-summary__free">FREE</span>}
              </div>
              {deliveryFee === 0 && membership?.active ? (
                <p className="co-summary__note co-summary__note--member">
                  🎉 Founding member — free delivery active until{" "}
                  {membership.expiresAt.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}.
                </p>
              ) : deliveryFee > 0 ? (
                <p className="co-summary__note">
                  Add {formatPrice(FREE_DELIVERY_MIN - total)} more for free delivery.
                </p>
              ) : null}
              <div className="co-summary__row co-summary__row--total">
                <span>Total payable</span>
                <span>{formatPrice(payable)}</span>
              </div>
            </div>
            <p className="co-summary__note">Final price confirmed after an availability check. Farm-fresh, quality guaranteed.</p>
            <div className="co-summary__trust">
              <span>🔒 Secure payments</span>
              <span>🌱 Farm fresh</span>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
